<script setup lang="ts">
import type { EventDetailDTO } from '~/shared/api/client/events/events.dto';
import DateBadge from '~/shared/ui/date-badge/DateBadge.vue';
import { formatTime } from '../lib/formatTime';
import { getEventHeroStub } from '~/shared/lib/getEventStubImage';
import { resizeImage, resizeImageSrcsetWidths } from '~/shared/lib/resizeImage';
import AddToCalendarButton from './AddToCalendarButton.vue';
import AdDisclosurePopover from '~/widgets/ad-disclosure/ui/AdDisclosurePopover.vue';

const props = defineProps<{
  event: EventDetailDTO;
  hasTimepadRegistration?: boolean;
  isParticipationDisabled?: boolean;
  onParticipationClick?: (event: Event) => void;
  showParticipationButton?: boolean;
  showCalendarButton?: boolean;
}>();

const timeRange = computed(() => {
  const start = formatTime(props.event.start_time);

  if (props.event.end_time_specified === false) {
    return start;
  }

  return `${start} - ${formatTime(props.event.end_time)}`;
});

const imageUrl = computed(() => {
  return props.event.image?.photo?.file_url ?? null;
});

const ctaUrl = computed(() => {
  return props.event.url || '';
});

const stubImage = computed(() => getEventHeroStub(props.event.id));

/*
  The box is close to square and never wider than 263x240. Cropping to the
  widest box keeps every narrower one from cutting the sides a second time.
*/
const IMAGE_ASPECT_RATIO = 263 / 240;

const imageSrc = computed(() =>
  resizeImage(imageUrl.value || stubImage.value, {
    width: 307,
    height: 307 / IMAGE_ASPECT_RATIO,
    mode: 'fill',
  }),
);

const imageSrcset = computed(() =>
  resizeImageSrcsetWidths(
    imageUrl.value || stubImage.value,
    [100, 200, 307, 420, 614, 921],
    { mode: 'fill', aspectRatio: IMAGE_ASPECT_RATIO },
  ),
);
</script>
<template>
  <div :class="$style.hero">
    <div :class="$style.content">
      <div :class="$style.meta">
        <DateBadge
          :start-time="event.start_time"
          :end-time="event.end_time"
          :month-only="event.month_only"
          variant="event-hero"
        />
        <div :class="$style.details">
          <p v-if="!event.month_only" :class="$style.time">{{ timeRange }}</p>
          <p :class="$style.place">{{ event.place }}</p>
        </div>
      </div>

      <h1 :class="$style.title">{{ event.name }}</h1>

      <div
        v-if="showParticipationButton || showCalendarButton"
        :class="$style.actions"
      >
        <Button
          v-if="showParticipationButton"
          theme="red"
          size="large"
          :disabled="isParticipationDisabled"
          :to="hasTimepadRegistration ? undefined : ctaUrl"
          :target="hasTimepadRegistration ? undefined : '_blank'"
          :class="$style.cta"
          @click="onParticipationClick"
        >
          Принять участие
        </Button>
        <AddToCalendarButton v-if="showCalendarButton" :event-id="event.id" />
      </div>
    </div>

    <div :class="$style.imageWrapper">
      <AdDisclosurePopover
        :erid="event.erid"
        :company="event.company_information"
      />
      <img
        :src="imageSrc"
        :srcset="imageSrcset || undefined"
        sizes="(max-width: 479px) 100px, (max-width: 599px) 191px, (max-width: 767px) 211px, (max-width: 1379px) 263px, 307px"
        :alt="event.name"
        :class="$style.image"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.hero {
  display: flex;
  align-items: stretch;
  padding: var(--space-2-5x);
  border-bottom: 1px solid var(--color-gray-line);
  gap: var(--space-2-5x);
  min-height: 320px;
}

.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--space-2-5x);
  min-width: 0;
  padding-bottom: var(--space-2-5x);
}

.meta {
  display: flex;
  gap: var(--space-4x);
  align-items: flex-start;
}

.details {
  display: flex;
  flex-direction: column;
  gap: var(--space-0-5x);
}

.time {
  @include text_caption1_caps;
  color: var(--color-black-1);
  margin: 0;
}

.place {
  @include text_caption1_caps;
  color: var(--color-black-1);
  margin: 0;
}

.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: var(--space-5x);
}

.cta {
  width: 216px;
  height: 50px;
  min-height: 50px;
  flex-shrink: 0;
}

.imageWrapper {
  position: relative;
  width: 307px;
  flex-shrink: 0;
  background-color: var(--color-gray-bg);
  overflow: hidden;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (max-width: 1379px) {
  .hero {
    min-height: auto;
  }

  .content {
    justify-content: flex-start;
    gap: var(--space-2-5x);
  }

  .meta {
    gap: var(--space-2-5x);
  }

  .title {
    @include text_h4;
  }

  .actions {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2-5x);
  }

  .imageWrapper {
    width: 263px;
    height: 240px;
  }
}

@media (max-width: 767px) {
  .imageWrapper {
    width: 211px;
    height: 192px;
  }
}

@media (max-width: 599px) {
  .hero {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto auto;
    gap: var(--space-2-5x);
    min-height: auto;
  }

  .content {
    display: contents;
  }

  .meta {
    grid-column: 1;
    grid-row: 1;
    flex-direction: column;
    gap: 10px;
  }

  .imageWrapper {
    grid-column: 2;
    grid-row: 1 / 3;
    width: 191px;
    height: 174px;
  }

  .title {
    grid-column: 1;
    grid-row: 2;
    @include text_h5;
  }

  .actions {
    grid-column: 1 / -1;
    grid-row: 3;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2-5x);
  }

  .time,
  .place {
    font-size: 9px;
    letter-spacing: 0.9px;
  }

  .cta {
    width: 141px;
    height: auto;
    min-height: 40px;
    padding: 7px 20px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
  }
}

@media (max-width: 479px) {
  .meta {
    flex-direction: row;
    grid-column: 1 / -1;
  }

  .title {
    grid-column: 1 / -1;
  }

  .actions {
    grid-column: 1;
    grid-row: 3;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }

  .imageWrapper {
    grid-column: 2;
    grid-row: 3;
    width: 100px;
    height: 95px;
  }
}
</style>
